import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { jwtDecode } from "jwt-decode";
import {
  clearStoredTokens,
  isJwtShaped,
  readStoredTokens,
  writeStoredTokens
} from "../services/tokenStorage";

/** The two values the server issues. Everything else is derived from the access token. */
interface Credentials {
  accessToken: string;
  refreshTokenId: string;
}

export interface AuthData extends Credentials {
  expiryTime: number | null;
  userId: string | null;
}

interface AuthContextValue {
  authData: AuthData | null;
  setTokens: (accessToken: string, refreshTokenId: string) => void;
  clearAuth: () => void;
  userId: string | null;
  /**
   * True when this provider was seeded with a pre-issued token (an embed / public dashboard) rather
   * than stored credentials. Such a session has no refresh token or MVC identity, so the ordinary
   * "refresh, else dead" recovery does not apply — only the page that exchanged can get another.
   */
  isEmbedSession: boolean;
  /**
   * Supplied by an embed page that can re-run its own token exchange. Consumers call this instead of
   * clearAuth on a session-dead response, so the dashboard recovers instead of staying anonymous.
   */
  onSessionUnrecoverable: (() => Promise<void> | void) | null;
}

interface AccessTokenClaims {
  exp?: number;
  sub?: string;
  nameid?: string;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within a AuthProvider");
  return ctx;
};

/**
 * Decodes once and returns both derived values. Reading `exp` and the subject with separate
 * `jwtDecode` calls parsed the same token twice on every render.
 */
function decodeClaims(accessToken: string): AccessTokenClaims | null {
  try {
    return jwtDecode<AccessTokenClaims>(accessToken);
  } catch (error) {
    console.error("Failed to decode access token:", error);
    return null;
  }
}

/**
 * `sub` is the standard claim; ASP.NET Identity emits `nameid`.
 *
 * A token that will not decode is not a credential. This used to log the failure and then hand
 * the undecodable value back anyway, so the rest of the app treated it as a live session: the
 * request interceptor either sent `Bearer undefined` or, when the value was a real `undefined`,
 * sent no Authorization header at all, and the page 401ed forever without ever reaching the
 * session-dead handling that would have refreshed it or sent the user to log in (AB#10507).
 */
function toAuthData(credentials: Credentials | null): AuthData | null {
  if (!credentials) return null;

  const claims = decodeClaims(credentials.accessToken);
  if (!claims) return null;

  return {
    ...credentials,
    expiryTime: typeof claims.exp === "number" ? claims.exp * 1000 : null,
    userId: claims.sub ?? claims.nameid ?? null
  };
}

function readInitialCredentials(initialAccessToken?: string): Credentials | null {
  // Embed pages obtain a JWT through the token exchange and never hold a refresh token,
  // so they cannot refresh — the empty id is what signals that.
  if (initialAccessToken) {
    return isJwtShaped(initialAccessToken)
      ? { accessToken: initialAccessToken, refreshTokenId: "" }
      : null;
  }

  // readStoredTokens only reports a usable pair, so a browser poisoned in the field starts
  // clean here rather than rehydrating the bad value on every mount.
  const { accessToken, refreshTokenId } = readStoredTokens();

  return accessToken && refreshTokenId ? { accessToken, refreshTokenId } : null;
}

interface AuthProviderProps {
  children: ReactNode;
  /** Pre-issued access token for embed pages, used instead of reading stored credentials. */
  initialAccessToken?: string;
  /**
   * Called when the session cannot be refreshed, for embed pages that can mint another token by
   * re-running their exchange. Replaces the clearAuth-and-go-anonymous default that left the public
   * dashboard sending header-less requests for the rest of its life (AB#9773).
   */
  onSessionUnrecoverable?: () => Promise<void> | void;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  initialAccessToken,
  onSessionUnrecoverable
}) => {
  // Only the issued values are state. expiryTime and userId are derived below rather than
  // stored, so they cannot fall out of step with the token they describe.
  const [credentials, setCredentials] = useState<Credentials | null>(() =>
    readInitialCredentials(initialAccessToken)
  );

  // An embed token is page-scoped: persisting it would overwrite the admin's credentials in the
  // same browser, and clearing on its behalf would delete them.
  const isEmbedSession = Boolean(initialAccessToken);

  const authData = useMemo(() => toAuthData(credentials), [credentials]);

  // A re-exchange replaces the seed prop rather than calling setTokens, so credentials must follow
  // it; otherwise the provider keeps serving the token it mounted with until a full remount.
  const seededToken = useRef(initialAccessToken);
  useEffect(() => {
    if (!initialAccessToken || initialAccessToken === seededToken.current) {
      return;
    }

    // Through readInitialCredentials rather than building the pair inline, so the mount path and
    // the re-exchange path apply the same guard. Built inline, this was the one entry point a
    // non-token could still get in through after the rest was hardened (AB#10507).
    const seeded = readInitialCredentials(initialAccessToken);
    if (!seeded) return;

    seededToken.current = initialAccessToken;
    setCredentials(seeded);
  }, [initialAccessToken]);

  const setTokens = useCallback(
    (accessToken: string, refreshTokenId: string) => {
      // Refuse rather than adopt. Callers are supposed to have validated already; if one has
      // not, keeping the credentials we have beats replacing them with something that is not a
      // token and cannot be refreshed back out of (AB#10507).
      if (!isJwtShaped(accessToken)) {
        console.error("Refusing an access token that is not a JWT (AB#10507)");
        return;
      }

      if (!isEmbedSession) {
        writeStoredTokens(accessToken, refreshTokenId);
      }

      // A blank id must not be written into an ordinary admin session's state. "" is the embed
      // sentinel meaning "this session can never refresh"; planted here it would make the next
      // rotation refuse without ever asking the server, and sign the admin out. Mirrors what
      // writeStoredTokens does with the same value: keep the id we already have.
      setCredentials((prev) => ({
        accessToken,
        refreshTokenId: isEmbedSession
          ? ""
          : refreshTokenId || prev?.refreshTokenId || ""
      }));
    },
    [isEmbedSession]
  );

  const clearAuth = useCallback(() => {
    if (!isEmbedSession) {
      clearStoredTokens();
    }

    setCredentials(null);
  }, [isEmbedSession]);

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      authData,
      setTokens,
      clearAuth,
      userId: authData?.userId ?? null,
      isEmbedSession,
      onSessionUnrecoverable: onSessionUnrecoverable ?? null
    }),
    [authData, setTokens, clearAuth, isEmbedSession, onSessionUnrecoverable]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
